if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.cleanupProductMediaModal();
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            this.replaceWishlistAppBlock(productElement, opener.getAttribute('data-product-url'));
            this.quickAddProductModalId = productElement.querySelector('product-modal')?.id || null;
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            // Run again on the live DOM in case SSR wishlist markup survived the parse/replace pass
            this.replaceWishlistAppBlock(this.modalContent, opener.getAttribute('data-product-url'));
            this.bindQuickViewWishlist(this.modalContent);
            this.bindProductMediaModalScrollLock();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      /**
       * product-modal moves itself to document.body, so clear it when quick-add closes.
       */
      cleanupProductMediaModal() {
        const modalId = this.quickAddProductModalId;
        this.quickAddProductModalId = null;
        if (!modalId) return;

        const modal = document.getElementById(modalId);
        if (!modal) return;

        if (modal.hasAttribute('open')) {
          modal.removeAttribute('open');
          removeTrapFocus(this.openedBy);
        }
        modal.remove();
      }

      /**
       * Closing the image lightbox removes body overflow-hidden; keep scroll locked
       * while quick-add is still open.
       */
      bindProductMediaModalScrollLock() {
        if (this._productMediaModalScrollLockBound) return;
        this._productMediaModalScrollLockBound = true;

        document.body.addEventListener('modalClosed', () => {
          if (this.hasAttribute('open')) {
            document.body.classList.add('overflow-hidden');
          }
        });
      }

      /**
       * Quick view injects a snapshot of the PDP (including Swish app-block HTML).
       * That markup can look "loaded" but clicks often do nothing in the modal.
       * Replace with a theme heart button wired to the Swish browser API.
       */
      replaceWishlistAppBlock(root, productUrl) {
        if (!root) return;

        const scope =
          root.matches?.('product-info') || root.querySelector?.('product-info')
            ? root.querySelector('product-info') || root
            : root;

        scope.querySelectorAll('wishlist-button-block').forEach((el) => {
          const wrap = el.closest('.shopify-block, .shopify-app-block');
          (wrap || el).remove();
        });
        scope.querySelectorAll('[data-wishlist-host], .product__wishlist-host').forEach((el) => el.remove());

        const title =
          scope.querySelector('.product__info-container > .product__title') ||
          scope.querySelector('.product__title');
        if (!title) return;

        let sibling = title.nextElementSibling;
        while (
          sibling &&
          (sibling.classList.contains('shopify-block') ||
            sibling.classList.contains('shopify-app-block') ||
            sibling.classList.contains('product__wishlist-host') ||
            sibling.matches?.('wishlist-button-block'))
        ) {
          const next = sibling.nextElementSibling;
          sibling.remove();
          sibling = next;
        }

        const doc = title.ownerDocument;
        const productInfo = scope.matches?.('product-info') ? scope : scope.querySelector('product-info') || scope;
        const titleLink = title.matches('a[href*="/products/"]')
          ? title
          : title.querySelector('a[href*="/products/"]');
        const linkHref = titleLink?.getAttribute('href') || productUrl || '';
        const handle =
          this.getProductHandleFromUrl(linkHref) || this.getProductHandleFromUrl(productUrl);
        const variantInput = scope.querySelector(
          'form[action*="/cart/add"] [name="id"], input.product-variant-id[name="id"], input[name="id"]'
        );
        const variantId =
          variantInput?.value ||
          this.getVariantIdFromUrl(linkHref) ||
          this.getVariantIdFromUrl(productUrl);
        const productId = productInfo?.dataset?.productId || '';

        const host = doc.createElement('div');
        host.className = 'product__wishlist-host shopify-block';
        host.setAttribute('data-wishlist-host', '');
        if (handle) host.setAttribute('data-product-handle', handle);
        if (variantId) host.setAttribute('data-variant-id', variantId);
        if (productId) host.setAttribute('data-product-id', productId);

        const button = doc.createElement('button');
        button.type = 'button';
        button.className = 'product__wishlist-qv';
        button.setAttribute('aria-label', 'Add to wishlist');
        button.setAttribute('aria-pressed', 'false');
        button.innerHTML =
          '<svg viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M16.7268 2.0751C15.105 0.530166 12.5216 0.695466 10.9438 2.2849L9.50434 3.74083L8.06487 2.2849C6.49341 0.695466 3.90368 0.530166 2.28193 2.0751C0.603617 3.6709 0.572179 6.34115 2.19392 7.98145L3.7528 9.55818L8.86947 14.7334C9.22148 15.0894 9.78721 15.0894 10.1329 14.7334L15.2496 9.55818L16.8085 7.98145C18.4239 6.34751 18.3988 3.67725 16.7205 2.0751H16.7268Z" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"></path></svg>';
        host.appendChild(button);

        title.insertAdjacentElement('afterend', host);
      }

      bindQuickViewWishlist(root) {
        const host = root.querySelector('[data-wishlist-host]');
        const button = host?.querySelector('.product__wishlist-qv');
        if (!host || !button || button.dataset.wishlistBound) return;
        button.dataset.wishlistBound = 'true';

        this.syncQuickViewWishlistState(host, button);

        button.addEventListener('click', async (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (button.dataset.loading === 'true') return;

          const productId = Number(host.dataset.productId || 0);
          const variantId = Number(host.dataset.variantId || 0);
          if (!productId || !variantId) {
            console.warn('[quick-view wishlist] Missing productId/variantId');
            return;
          }

          button.dataset.loading = 'true';
          button.setAttribute('aria-busy', 'true');

          try {
            const swish = await this.getSwishApp();
            const selected = button.getAttribute('aria-pressed') === 'true';

            if (selected) {
              const existing =
                (host.dataset.wishlistItemId
                  ? { id: host.dataset.wishlistItemId }
                  : null) || (await this.findWishlistItem(swish, productId, variantId));
              if (existing?.id) {
                const { error } = await swish.api.items.deleteById(existing.id);
                if (error) throw error;
              }
              delete host.dataset.wishlistItemId;
              this.setWishlistButtonState(button, false);
            } else {
              const { data, error } = await swish.api.items.create({
                productId,
                variantId,
                quantity: 1,
              });
              if (error) throw error;
              const createdId = data?.id || data?.item?.id;
              if (createdId) host.dataset.wishlistItemId = String(createdId);
              this.setWishlistButtonState(button, true);
            }
          } catch (error) {
            console.warn('[quick-view wishlist]', error);
          } finally {
            button.dataset.loading = 'false';
            button.removeAttribute('aria-busy');
          }
        });
      }

      /**
       * Custom QV heart — UI is theme-owned. Persistence still goes through Swish
       * via the app proxy (/apps/wishlist/api). Prefer an already-booted window.swish,
       * otherwise use a small proxy client (no Swish button embed required).
       */
      async getSwishApp() {
        if (window.__poppySwishApp?.api) return window.__poppySwishApp;
        if (window.swish?.api) {
          window.__poppySwishApp = window.swish;
          return window.__poppySwishApp;
        }

        // Short wait in case the app embed finishes booting mid-click
        const embed = await new Promise((resolve) => {
          if (window.swish?.api) return resolve(window.swish);
          const onReady = () => {
            cleanup();
            resolve(window.swish);
          };
          const cleanup = () => {
            document.removeEventListener('swish-ready', onReady);
            clearTimeout(timeout);
          };
          document.addEventListener('swish-ready', onReady);
          const timeout = setTimeout(() => {
            cleanup();
            resolve(window.swish);
          }, 300);
        });

        if (embed?.api) {
          window.__poppySwishApp = embed;
          return window.__poppySwishApp;
        }

        window.__poppySwishApp = this.createSwishProxyClient();
        return window.__poppySwishApp;
      }

      createSwishProxyClient() {
        const baseUrl = '/apps/wishlist/api';
        const apiVersion = '2025-10';

        const getProfile = () => {
          try {
            const existing = localStorage.getItem('swish-profile');
            if (existing) return existing;
            const legacy = localStorage.getItem('wk_session_id');
            if (!legacy) return null;
            const migrated = `gid://swish/Session/${legacy}`;
            localStorage.setItem('swish-profile', migrated);
            localStorage.removeItem('wk_session_id');
            return migrated;
          } catch (e) {
            return null;
          }
        };

        const storeProfile = (value) => {
          try {
            if (value) localStorage.setItem('swish-profile', value);
            else localStorage.removeItem('swish-profile');
          } catch (e) {
            /* private mode */
          }
        };

        const request = async (path, { method = 'GET', body } = {}) => {
          const headers = {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Swish-Api-Version': apiVersion,
          };
          const profile = getProfile();
          if (profile) headers.Profile = profile;

          const response = await fetch(`${baseUrl}${path}`, {
            method,
            headers,
            credentials: 'same-origin',
            body: body === undefined ? undefined : JSON.stringify(body),
          });

          const setProfile = response.headers.get('Set-Profile');
          if (setProfile != null) storeProfile(setProfile || null);

          let payload = null;
          try {
            payload = await response.json();
          } catch (e) {
            payload = null;
          }

          if (!response.ok) {
            return {
              data: null,
              error: payload?.error || payload || new Error(`Wishlist request failed (${response.status})`),
            };
          }

          // Swish responses are usually { data, pageInfo? }; tolerate raw payloads too
          if (payload && Object.prototype.hasOwnProperty.call(payload, 'data')) {
            return { data: payload.data, pageInfo: payload.pageInfo, error: null };
          }
          return { data: payload, error: null };
        };

        const normalizeItemId = (itemId) => {
          if (!itemId) return '';
          if (typeof itemId === 'object') {
            return normalizeItemId(itemId.id || itemId.itemId);
          }
          const value = String(itemId);
          // App proxy path deletes break on full GIDs; API examples use the bare id
          const gidMatch = value.match(/\/(?:Item|Items)\/([^/?#]+)/i);
          if (gidMatch) return gidMatch[1];
          return value;
        };

        return {
          api: {
            items: {
              create: (item) => request('/items', { method: 'POST', body: item }),
              list: (query = {}) => {
                const params = new URLSearchParams();
                if (query.limit != null) params.set('limit', String(query.limit));
                if (query.query) params.set('query', query.query);
                if (query.page) params.set('page', String(query.page));
                const qs = params.toString();
                return request(`/items${qs ? `?${qs}` : ''}`);
              },
              // Prefer body delete — avoids GID/path encoding 400s through the app proxy
              deleteById: async (itemId) => {
                const id = normalizeItemId(itemId);
                if (!id) {
                  return { data: null, error: new Error('Missing wishlist item id') };
                }

                const bulk = await request('/items', {
                  method: 'DELETE',
                  body: { itemIds: [{ id }] },
                });
                if (!bulk.error) return bulk;

                return request(`/items/${encodeURIComponent(id)}`, { method: 'DELETE' });
              },
            },
          },
        };
      }

      async syncQuickViewWishlistState(host, button) {
        try {
          const productId = Number(host.dataset.productId || 0);
          const variantId = Number(host.dataset.variantId || 0);
          if (!productId || !variantId) return;

          const swish = await this.getSwishApp();
          const existing = await this.findWishlistItem(swish, productId, variantId);
          if (existing?.id) host.dataset.wishlistItemId = String(existing.id);
          this.setWishlistButtonState(button, Boolean(existing));
        } catch (error) {
          // Leave default unselected state if wishlist isn't reachable yet
        }
      }

      async findWishlistItem(swish, productId, variantId) {
        const listItems = (result) => {
          if (!result || result.error) return [];
          if (Array.isArray(result.data)) return result.data;
          if (Array.isArray(result.data?.items)) return result.data.items;
          if (Array.isArray(result.data?.results)) return result.data.results;
          return [];
        };

        const byVariant = await swish.api.items.list({
          query: `variant:${variantId}`,
          limit: 1,
        });
        const variantMatch = listItems(byVariant).find(
          (item) => Number(item.variantId) === variantId
        );
        if (variantMatch) return variantMatch;

        const listed = await swish.api.items.list({ limit: 100 });
        if (listed.error) throw listed.error;
        return (
          listItems(listed).find((item) => Number(item.variantId) === variantId) ||
          listItems(listed).find((item) => Number(item.productId) === productId) ||
          null
        );
      }

      setWishlistButtonState(button, selected) {
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.setAttribute('aria-label', selected ? 'In wishlist' : 'Add to wishlist');
      }

      getProductHandleFromUrl(url) {
        if (!url) return '';
        try {
          const pathname = new URL(url, window.location.origin).pathname;
          const parts = pathname.split('/').filter(Boolean);
          const index = parts.indexOf('products');
          return index >= 0 ? decodeURIComponent(parts[index + 1] || '') : '';
        } catch (e) {
          return '';
        }
      }

      getVariantIdFromUrl(url) {
        if (!url) return '';
        try {
          return new URL(url, window.location.origin).searchParams.get('variant') || '';
        } catch (e) {
          return '';
        }
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        // Keep product-modal so gallery image zoom (product__media-toggle) works in quick-add.
        // It is cleaned up in hide() after ModalDialog moves it to document.body.

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        productElement.querySelectorAll('sticky-atc').forEach((stickyAtc) => stickyAtc.remove());

        productElement.querySelectorAll('.product-tabs, [data-product-tabs]').forEach((tabs) => tabs.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
